import { useEffect, useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import clsx from 'clsx';
import { cloneDeep, remove, get, isEqual, isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  HttpResponse,
  SectionDto,
  SectionUpdateDto,
  SectionResponseDto,
  QuestionGroupDto,
  QuestionResponseDto,
  QuestionDto,
  SectionResponseUpdatedDto,
  ScoringInformationDto,
} from 'models/dtoTypes';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import { useHistory } from 'react-router';
import { generateSectionMenus } from 'layouts/menuItems';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
// import Actionbar from 'components/Actionbar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import SmeConfig from 'data/SmeConfig';
import Formsy from 'formsy-react';
import Grid from '@mui/material/Grid';
import KitemarkModal from 'dialogs/KitemarkModal';
import Question from 'components/Question';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SmePrompt from 'components/SmePrompt';
import ToastAlert from 'components/ToastAlert';
import CurrentUser from 'auth/currentUser';
import { Accordion as MuiAccordion, AccordionDetails, AccordionProps, AccordionSummary } from '@mui/material';

import {
  DeleteSubSectionResponse,
  DeleteFile
} from 'services/openapiClient';

import { CreateOrUpdateResponse, GetSection } from 'services/section';
import matchConditions from 'utils/matchConditions';
import { generateSingleResponse, getFieldWidget, getSubSection, getSubSectionResponses } from 'utils/sectionUtils';
import { FileValue, ValueType } from 'typings/question';
// import { drawerWidth } from 'atlasConfig';

const PREFIX = 'Questionnaire';

const classes = {
  root: `${PREFIX}-root`,
  buttons: `${PREFIX}-buttons`,
  submitButton: `${PREFIX}-submitButton`,
  flashSpacer: `${PREFIX}-flashSpacer`,
  groupContainer: `${PREFIX}-groupContainer`,
  orphanQuestion: `${PREFIX}-orphanQuestion`,
  groupQuestion: `${PREFIX}-groupQuestion`,
  groupQuestionFirst: `${PREFIX}-groupQuestion-first`,
  groupQuestionLast: `${PREFIX}-groupQuestion-last`,
  boxContainer: `${PREFIX}-boxContainer`,
  actionbarContainer: `${PREFIX}-actionbarContainer`,
  blackSpacer: `${PREFIX}-blackSpacer`,
  actionbar: `${PREFIX}-actionbar`
};

const Root = styled('div')(({ theme }) => ({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',

  [`& .${classes.root}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },

  [`& .${classes.buttons}`]: {
    width: 'inherit',
    minWidth: 400,
    position: 'sticky',
    right: 10,
    marginTop: 50,
    '& > *': {
      textAlign: 'center'
    }
  },

  [`& .${classes.submitButton}`]: {
    minWidth: 150,
    height: 50
  },

  [`& .${classes.flashSpacer}`]: {
    minHeight: 10,
  },

  // blue line for whole group questions
  [`& .${classes.groupContainer}`]: {
    position: 'relative',
    '&::before': {
      content: '""',
      display: 'inline-block',
      position: 'absolute',
      left: 6,
      top: 0,
      bottom: 0,
      width: 2,
      backgroundColor: theme.palette.primary.main,
    },
  },

  [`& .${classes.orphanQuestion}`]: {
    marginLeft: 30,
  },

  [`& .${classes.groupQuestion}`]: {
    position: 'relative',
    marginLeft: 30,
    // show blue circles aligned with the blue line set above
    '&::before': {
      content: '""',
      lineHeight: '10px',
      fontSize: '11px',
      padding: '1px 1.5px',
      display: 'inline-block',
      position: 'absolute',
      left: -30,
      top: '50%',
      marginTop: -7,
      width: 14,
      height: 14,
      borderRadius: 2,
      borderWidth: 2,
      borderStyle: 'solid',
      borderColor: theme.palette.primary.main,
      backgroundColor: 'white',
      zIndex: 1,
    },
    '&-first::before': {
      top: 30,
    },
    '&-last::before': {
      // top: '50%',
    },
    '&-last::after': {
      top: 30,
    },
    [`&-last:not(.${classes.groupQuestion}-first)::after`]: {
      top: '50%', // is last but not the first
    },
    // first question, white bar to cover the top line of group
    '&:first-of-type .groupQuestionGoust': {
      position: 'absolute',
      marginTop: -7,
      width: 10,
      left: -28,
      background: 'white',
      top: 0,
      height: 30,
    },
    '&.conditionalQuestion': {
      position: 'relative',
      marginLeft: 60,
      marginTop: 0,
      padding: 5,
    },
    '&.conditionalQuestion::after': {
      content: '""',
      display: 'inline-block',
      position: 'absolute',
      left: -54,
      width: 25,
      top: '50%',
      height: 2,
      backgroundColor: theme.palette.primary.main,
    },
    // last question is nested question, hide blue line tail in group
    '&.conditionalQuestion:last-child::after': {
      content: '""',
      display: 'inline-block',
      position: 'absolute',
      left: -54,
      top: '50%',
      width: 25,
      height: '50%',
      borderLeft: '3px white solid',
      borderTopColor: theme.palette.primary.main,
      borderTopWidth: 2,
      borderTopStyle: 'solid',
      backgroundColor: 'transparent',
    },
    '&.blank::before': {
      backgroundColor: 'white'
    },
    '&.answered::before': {
      borderColor: theme.palette.primary.main,
      content: '"✓"',
      color: theme.palette.primary.main,
    },
    '&.blank.required::before': {
      borderColor: 'red',
      content: '"✗"',
      color: 'red',
    },

    // last item show a white line to hide the unwantted tail of the blue line above
    '&:not(.conditionalQuestion):last-child::after': {
      content: '""',
      display: 'inline-block',
      position: 'absolute',
      left: -30,
      // top: 'calc(100% - 37px)',
      marginTop: -7,
      bottom: 0,
      width: 10,
      backgroundColor: 'white',
    },
  },

  [`& .${classes.boxContainer}`]: {
    flex: 1,
    padding: 30,
    width: '100%',
    backgroundColor: 'white',
  },

  [`& .${classes.actionbarContainer}`]: {
    // position: 'fixed',
    // zIndex: 1000,
    // height: 150, // make item height fixed
    // width: `calc(100% - ${drawerWidth}px)`,
  },

  [`& .${classes.blackSpacer}`]: {
    minHeight: 20,
    backgroundColor: '#474747',
  },

  [`& .${classes.actionbar}`]: {
    width: '100%',
    backgroundColor: 'white',
  }
}));

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '5px',
  marginBottom: '20px',
  '&.Mui-expanded': {
    marginBottom: '20px',
  },
  '&:before': {
    display: 'none',
  },
}));

interface Props {
  alias: string;
  config: SmeConfig;
  companyId?: string;
}

type QuestionGroup = QuestionGroupDto & {
  questions: QuestionDto[],
  unsatisfiedQuestions: string[];
  hiddenQuestions: string[];
};

function Questionnaire(props: Props): JSX.Element {
  const formRef = useRef<Formsy | null>(null);
  const mountedRef = useRef(true);
  const history = useHistory();
  const { alias, config, companyId } = props;
  const [httpResponse, setHttpResponse] = useState<HttpResponse | undefined>(undefined);
  const [isFormValid, setIsFormValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [section, setSection] = useState<SectionDto | undefined>(undefined);
  const [questionGroups, setQuestionGroups] = useState<QuestionGroup[]>([] as QuestionGroup[]);
  const { t } = useTranslation('default');

  const isChanged = () => {
    const formsyEl = formRef.current;
    return formsyEl && typeof formsyEl.isChanged === 'function' ? formsyEl.isChanged() : false;
  };

  const getInitialValues = (sectionData: SectionDto) => {
    if (!sectionData) {
      return {};
    }
    const currentValues = {};
    sectionData.sectionResponse?.responses?.map(res => {
      currentValues[res.questionAlias] = (get(res, 'value') ?? get(res, 'selectedOptions')) as ValueType;
    });
    return currentValues;
  };

  const getCurrentValues = () => {
    const formsyEl = formRef.current;
    return (formsyEl ? formsyEl.getCurrentValues() : {}) as FormData;
  };

  const generateQuestionGroups = (sectionData: SectionDto): QuestionGroup[] => {
    const currentValues = isChanged() ? getCurrentValues() : getInitialValues(sectionData);
    const { questions } = sectionData;
    const subSectionResponses = sectionData?.sectionResponse?.subSectionResponses || [];
    const unsatisfiedQuestions = sectionData?.sectionResponse?.unsatisfiedQuestions || [];

    if (!sectionData || !questions) {
      return [];
    }

    const groups: QuestionGroup[] = [];

    let currentGroup: QuestionGroup;

    questions.forEach(question => {
      if (!currentGroup || currentGroup.alias !== question.questionGroupAlias) {
        const alias = question.questionGroupAlias || `group-${groups.length}`;
        const group = sectionData.questionGroups?.find(g => g.alias === question.questionGroupAlias);
        const { label, description, isCollapse } = group || {};
        currentGroup = {
          alias,
          label,
          description,
          isCollapse,
          questions: [],
          unsatisfiedQuestions: [],
          hiddenQuestions: [],
        };
        groups.push(currentGroup);
      }
      if (!question.alias) {
        return;
      }
      const localValue = currentValues[question.alias];
      const isZero = localValue === 0;
      const hasLocalValue = Array.isArray(localValue) ? !isEmpty(localValue) : (isZero || Boolean(localValue));
      const hasNoFile = question.type === 'QuestionFileListDto' && unsatisfiedQuestions.includes(question.alias);
      const hasSubSectionResponse = question.type === 'QuestionSubSectionListDto' && subSectionResponses.find(res => res.questionAlias === question.alias);
      const hasNoValue = question.type !== 'QuestionFileListDto' &&
        question.type !== 'QuestionBoolDto' &&
        !hasLocalValue;
      // file questions should always checking sectionData.sectionResponse.unsatisfiedQuestions
      if ((hasNoFile || hasNoValue) && !hasSubSectionResponse) {
        currentGroup.unsatisfiedQuestions.push(question.alias);
      }
      if (question.alias && !matchConditions(question.conditions, currentValues)) {
        currentGroup.hiddenQuestions.push(question.alias);
      }
      currentGroup.questions.push(question);
    });

    return groups;
  };

  const generateQuestionResponses = (model: FormData): QuestionResponseDto[] => {
    if (!section) return [];
    const questionMapper: { [alias: string]: string } = {};
    const { questions } = section;
    if (Array.isArray(questions)) {
      questions.forEach((field) => {
        if (field.alias) {
          questionMapper[field.alias] = field.type;
        }
      });
    }
    const questionResponses = [];
    const fields = Object.entries(model).filter(f => f[1] !== undefined);

    for (const field of fields) {
      const [questionAlias, value] = field;
      const type = questionMapper[questionAlias];
      if (type === 'QuestionFileListDto') {
        const files = (Array.isArray(value) ? value : []) as FileValue[];
        for (const file of files) {
          const response = generateSingleResponse(questionAlias, 'QuestionFileListDto', file);
          questionResponses.push(response);
        }
      } else {
        const response = generateSingleResponse(questionAlias, type, value);
        questionResponses.push(response);
      }
    }
    return questionResponses;
  };


  const cacheCurrentSectionResponse = (sectionResponse: SectionResponseDto) => {
    const { setCurrentSectionResponse } = config;
    setCurrentSectionResponse && setCurrentSectionResponse(sectionResponse);
  };

  // get the current section schema
  const fetchData = async () => {
    if (mountedRef.current) {
      setHttpResponse(undefined);
      setLoading(true);
    }
    const response = await GetSection(alias || 'onboarding', companyId);

    const data = response?.data;
    if (!mountedRef.current) {
      return;
    }
    if (data) {
      cacheCurrentSectionResponse(data.sectionResponse as SectionResponseDto);
      setSection(data);
      setQuestionGroups(generateQuestionGroups(data));
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchData();
  }, []);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const handleChange = () => {
    if (!section) {
      return;
    }
    const newQuestionGroups = generateQuestionGroups(section);
    if (!isEqual(newQuestionGroups, questionGroups)) {
      setQuestionGroups(newQuestionGroups);
    }
  };

  const handleDeleteSubSectionResponse = async (subSuctionAlias: string, subSectionResponseId: string) => {
    const response = await DeleteSubSectionResponse({
      sectionAlias: section?.alias || '',
      subSectionResponseId,
      alias: subSuctionAlias
    });
    const newSection = cloneDeep(section);
    const oldSubSectionResponses = newSection?.sectionResponse?.subSectionResponses;
    if (response?.status === 200 && Array.isArray(oldSubSectionResponses)) {
      // delete fileId in section.responses and section.sectionResponse.subSectionResponses
      remove(oldSubSectionResponses, (res) => get(res, 'id') === subSectionResponseId);
      setSection(newSection);
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (typeof section?.alias === 'string' && typeof fileId === 'string') {
      const response = await DeleteFile({ sectionAlias: section?.alias, fileResponseId: fileId });
      if (response?.status === 200) {
        const sectionResponse = response.data;
        setSection({ ...section, sectionResponse: sectionResponse, scoringInformation: sectionResponse.scoringInformation });
      }
    }
  };

  const disableButton = () => {
    setIsFormValid(false);
  };

  const enableButton = () => {
    setIsFormValid(true);
  };

  const goToAlias = (alias?: string): void => {
    const formatAlias = alias ?? '';
    history.push(`${formatAlias}`);
  };

  const goToAliasWithAutoSave = async (nextAlias?: string): Promise<void> => {
    if (!isChanged() || !section?.alias) {
      goToAlias(nextAlias);
      return;
    }

    // auto save
    const alias = section?.alias || '';
    const model = getCurrentValues();
    const questionResponses = generateQuestionResponses(model);
    setSubmitting(true);
    const response = await CreateOrUpdateResponse(
      {
        alias,
        updateScore: false
      },
      {
        sectionAlias: alias,
        companyId,
        questionResponses,
        subSectionResponses: []
      },
      setHttpResponse
    );

    if (response?.status === 200) {
      goToAlias(nextAlias);
      return;
    }
    setHttpResponse(response);
    setSubmitting(false);
  };


  async function handleSubmit(model: FormData) {
    if (!section || !section.alias) return;
    const { alias } = section;
    const questionResponses = generateQuestionResponses(model);
    // const questionResponses: QuestionResponse[] = [{questionAlias: 'name', value: 'alba', type: 'QuestionTextDto'}];
    setSubmitting(true);
    const response = await CreateOrUpdateResponse(
      {
        alias,
        updateScore: false
      },
      {
        sectionAlias: alias,
        companyId,
        questionResponses,
        subSectionResponses: []
      },
      setHttpResponse
    );

    if (response?.status === 200) {
      const sectionResponse = response.data;
      // cache the response of current section
      cacheCurrentSectionResponse(sectionResponse as SectionResponseDto);
      // Section state change should update sidebar items;
      if (section.sectionResponseState !== sectionResponse.state) {
        // TODO: move set context into useEffect
        const { sections, setSections } = config;
        const newSections = cloneDeep(sections);
        const currentSection = newSections.find(sec => sec.alias === section.alias);
        if (currentSection) {
          currentSection.sectionResponseState = sectionResponse.state;
          setSections && setSections(newSections);
        }
      }
      // update company-information should update cached currentUser and refresh sidebar
      if (alias === 'company-information') {
        await CurrentUser.getInstance().refresh(config);
      }
      // Use this to fresh response
      setSection({
        ...section,
        sectionResponse,
        sectionResponseState: sectionResponse.state,
        scoringInformation: sectionResponse.scoringInformation
      });
    }
    setHttpResponse(response);
    setSubmitting(false);
  }

  const handleSubmitSubSectionResponse = async (questionAlias: string, questionResponses: QuestionResponseDto[], subSectionId?: string) => {
    const { alias } = section as SectionDto;
    if (questionResponses.length === 0 || !questionAlias || !alias) {
      throw new Error('invalid state for submittion');
    }
    const subSectionResponses = [{
      subSectionId,
      questionAlias,
      questionResponses
    }];
    const response = await CreateOrUpdateResponse(
      {
        alias,
        updateScore: false,
      },
      {
        sectionAlias: alias,
        companyId,
        questionResponses: [],
        subSectionResponses
      }
    );

    if (response?.status === 200) {
      const sectionResponse = response.data as SectionResponseDto;
      // Use this fresh response
      // section.sectionResponse = sectionResponse;
      setSection({ ...section, sectionResponse });
    }
    return response;
  };

  const handleUpdateSection = (sectionResponse: SectionResponseUpdatedDto) => {
    setSection({ ...section, sectionResponse, scoringInformation: sectionResponse.scoringInformation });
  };

  // generate POST data for single file upload
  const handleGenerateSectionFileData = (questionAlias: string, data: string, fileName: string, extension: string): SectionUpdateDto => {
    const body = {
      companyId: companyId,
      sectionAlias: section?.alias || '',
      subSectionResponses: [],
      questionResponses: [{
        questionAlias,
        type: 'QuestionResponseFileDto',
        data,
        fileName,
        extension
      }]
    };
    return body;
  };

  if (loading || submitting) {
    return (
      <div style={{ margin: 20 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box sx={{ mr: 1 }}>
            <CircularProgress />
          </Box>
          <Box sx={{ minWidth: 35, flex: 1, }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: '5px', fontSize: 18, }}>
              {submitting ? 'Saving...' : 'Preparing questions...'}
            </Typography>
          </Box>
        </Box>
      </div>
    );
  }

  const getResponses = (field: QuestionDto, response?: SectionResponseDto): QuestionResponseDto[] => {
    if (response == null) {
      return [];
    }
    return response.responses.filter(r => r.questionAlias === field.alias);
  };

  const renderQuestion = (
    field: QuestionDto,
    isGrouping: boolean,
    isBlank: boolean,
    isFirst = false,
    isLast = false,
    isRequired = false,
  ) => {
    if (!section) return;
    const importedProps = {};
    // include subSection when subSectionAlias exists
    if (field.type === 'QuestionSubSectionListDto') {
      const subSectionAlias = (get(field, 'subSectionAlias') || '') as string;
      importedProps['subSection'] = getSubSection(section, subSectionAlias);
      importedProps['subSectionResponses'] = getSubSectionResponses(section, subSectionAlias);
      importedProps['onDeleteSubSectionResponse'] = handleDeleteSubSectionResponse;
    }

    // if a claiming in progress, make all field readonly;
    const statusLabel = CurrentUser.getInstance().getCompanyAttr('status');
    if (statusLabel === 'Claiming') {
      importedProps['readOnly'] = true;
    }
    const { alias, conditions } = field;
    const isConditional = conditions.length > 0;
    const className = clsx(
      isGrouping ? classes.groupQuestion : classes.orphanQuestion,
      isFirst ? classes.groupQuestionFirst : '',
      isLast ? classes.groupQuestionLast : '',
      isConditional ? 'conditionalQuestion' : '',
      isBlank ? 'blank' : 'answered',
      isRequired ? 'required' : 'optional',
    );
    const action = (section?.scoringInformation || []).find(si => alias && si.questionAlias === alias);
    return (
      <div
        key={alias}
        className={className}
      >
        <div className="groupQuestionGoust" />
        <Question
          {...importedProps}
          questionDto={field}
          sectionAlias={section?.alias}
          action={action ? action : undefined}
          onSubmitSubSectionResponse={handleSubmitSubSectionResponse}
          widget={getFieldWidget(field)}
          onDeleteFile={handleDeleteFile}
          onGeneratePostFileData={handleGenerateSectionFileData}
          onUpdateSection={handleUpdateSection}
          responses={getResponses(field, section?.sectionResponse)}
        />
      </div>
    );
  };

  const renderQuestionGroup = (group: QuestionGroup) => {
    const { questions, unsatisfiedQuestions, hiddenQuestions } = group;
    const isGrouping = questions.length > 1;
    const firstVisibleQuestion = (group.questions || []).find(q => !hiddenQuestions.includes(q.alias || ''));
    const lastVisibleQuestion = cloneDeep((group.questions || [])).reverse().find(q => !hiddenQuestions.includes(q.alias || ''));

    if (!group.isCollapse) {
      return (
        <div key={group.alias}>
          {group.label && <h3>{group.label}</h3>}
          {group.description && <h4>{group.description}</h4>}
          <div className={isGrouping ? classes.groupContainer : ''}>
            {group.questions.map((question) => {
              const isHidden = hiddenQuestions.includes(question.alias || '');
              const isBlank = unsatisfiedQuestions.includes(question.alias || '');
              const isFirst = firstVisibleQuestion?.alias === question.alias;
              const isLast = lastVisibleQuestion?.alias === question.alias;
              const isRequired = question?.required && !question?.readOnly;
              return isHidden ? null : renderQuestion(question, isGrouping, isBlank, isFirst, isLast, isRequired);
            })}
          </div>
        </div>
      );
    }

    return (
      <Accordion>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={group.alias}
          id={group.alias}
        >
          {group.label && <h3>{group.label}</h3>}
          {!group.label && group.alias && <h3>{group.alias}</h3>}
          {group.description && <h4>{group.description}</h4>}
        </AccordionSummary>
        <AccordionDetails>
          <div className={isGrouping ? classes.groupContainer : ''}>
            {group.questions.map((question, index) => {
              const isHidden = hiddenQuestions.includes(question.alias || '');
              const isBlank = unsatisfiedQuestions.includes(question.alias || '');
              return isHidden ? null : renderQuestion(question, isGrouping, isBlank, index === 0, index === group.questions.length - 1);
            })}
          </div>
        </AccordionDetails>
      </Accordion>
    );
  };

  const renderToastAlert = () => {
    if (!httpResponse) return <></>;
    const open = Boolean(httpResponse);
    const message = httpResponse.status === 200 ? 'Your answers have been saved' : 'The responses are not valid.';
    const severity = httpResponse.status === 200 ? 'success' : 'error';
    const scoringUpdates = get(httpResponse, ['data', 'scoringUpdates']) as ScoringInformationDto[] || [];
    return (
      <ToastAlert
        open={open}
        scoringUpdates={scoringUpdates}
        severity={severity}
        message={message}
        onClose={() => setHttpResponse(undefined)}
      />
    );
  };

  const renderForm = () => {
    const sections = config.sections;
    const sectionMenus = generateSectionMenus(sections);
    const currentIndex = sections.map(s => s.alias).indexOf(alias);
    const enablePrevious = currentIndex > 0 && !sectionMenus[currentIndex - 1]['lock'];
    const enableNext = currentIndex < sections.length - 1 && !sectionMenus[currentIndex + 1]['lock'];
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Formsy
          autoComplete="off"
          onValidSubmit={(model: FormData) => handleSubmit(model)}
          onValid={enableButton}
          onChange={handleChange}
          onInvalid={disableButton}
          ref={formRef}
          className="flex flex-col justify-center"
        >

          {questionGroups.map(renderQuestionGroup)}
          <Grid container className={classes.buttons} spacing={1}>
            <Grid item xs={3} >
              {enablePrevious && (
                <div>
                  <IconButton
                    color="primary"
                    aria-label="previous"
                    disabled={!isFormValid}
                    onClick={() => goToAliasWithAutoSave(sections[currentIndex - 1]['alias'])}
                    size="large">
                    <ArrowBackIosIcon fontSize="large" />
                    <span style={{ fontSize: 18 }}>Back</span>
                  </IconButton>
                </div>
              )}
            </Grid>

            <Grid item xs={6} >
              <Button
                type="submit"
                variant="contained"
                color="primary"
                className={classes.submitButton}
                aria-label="Signup"
                disabled={!isFormValid}
              >
                {t('save')}
              </Button>
            </Grid>

            <Grid item xs={3} >
              {enableNext && (
                <div>
                  <IconButton
                    color="primary"
                    aria-label="next"
                    disabled={!isFormValid}
                    onClick={() => goToAliasWithAutoSave(sections[currentIndex + 1]['alias'])}
                    size="large">
                    <span style={{ fontSize: 18 }}>Save and continue</span>
                    <ArrowForwardIosIcon fontSize="large" />
                  </IconButton>
                </div>
              )}
            </Grid>
          </Grid>
        </Formsy>
      </LocalizationProvider>
    );
  };

  const shouldShowDirtyWarning = (): string | boolean => {
    const message = 'Leaving this page will cause your unsaved changes to be discarded. Are you sure you want to continue?';
    return isChanged() ? message : true;
  };

  // kitemark-level-1-complete
  const openKitemark = (section?.scoringInformation || []).find(si => alias && si.scoringRuleAlias === 'kitemark-level-1-complete');

  // const renderActionbar = () => {
  //   return (
  //     <div className={classes.actionbarContainer}>
  //       <div className={classes.blackSpacer} />
  //       <Box className={classes.actionbar}>
  //         <Actionbar
  //           items={section?.scoringInformation || []}
  //         />
  //       </Box>
  //     </div>
  //   );
  // };

  return (
    <Root>
      {/* {renderActionbar()} */}
      <Box className={classes.boxContainer}>
        {renderToastAlert()}
        <div className={classes.root}>
          <Typography variant="h5" align="left">
            <span>{section?.label} </span>
          </Typography>
          <div className={classes.flashSpacer} />
          <SmePrompt when={true} message={shouldShowDirtyWarning} />
          {renderForm()}
        </div>
      </Box>
      <KitemarkModal
        open={Boolean(openKitemark)}
      />
    </Root>
  );
}

export default Questionnaire;
